import React, { useRef } from 'react';
import * as htmlToImage from 'html-to-image';
import { HiDownload } from 'react-icons/hi';
import { validateChartConfig } from './validateChartConfig';
import BarChartRenderer from './renderers/BarChartRenderer';
import LineAreaRenderer from './renderers/LineAreaRenderer';
import PieDonutRenderer from './renderers/PieDonutRenderer';
import TableRenderer from './renderers/TableRenderer';
import KpiRenderer from './renderers/KpiRenderer';
import GaugeRenderer from './renderers/GaugeRenderer';
import ScatterRenderer from './renderers/ScatterRenderer';
import './DynamicChart.css';

const DynamicChart = ({ config, compact = false, hideTitle = false }) => {
  const chartRef = useRef(null);
  const validated = validateChartConfig(config);
  
  if (!validated) {
    return <div className={`dc-error ${compact ? 'compact' : ''}`}>Unable to render chart</div>;
  }

  const props = { data: validated.data, config: validated.config, compact };

  let ChartContent;

  switch (validated.chartType) {
    case 'bar':        ChartContent = <BarChartRenderer {...props} />; break;
    case 'stackedBar': ChartContent = <BarChartRenderer {...props} stacked={true} />; break;
    case 'line':
    case 'area':       ChartContent = <LineAreaRenderer {...props} type={validated.chartType} />; break;
    case 'pie':
    case 'donut':      ChartContent = <PieDonutRenderer {...props} donut={validated.chartType === 'donut'} />; break;
    case 'scatter':    ChartContent = <ScatterRenderer {...props} />; break;
    case 'table':      ChartContent = <TableRenderer {...props} />; break;
    case 'kpi':        ChartContent = <KpiRenderer {...props} />; break;
    case 'gauge':      ChartContent = <GaugeRenderer {...props} />; break;
    default:           ChartContent = <BarChartRenderer {...props} />; break; // Fallback to Bar Chart
  }

  const handleExport = async () => {
    if (chartRef.current) {
      try {
        const dataUrl = await htmlToImage.toPng(chartRef.current, { backgroundColor: '#ffffff' });
        const link = document.createElement('a');
        link.download = `max-ai-chart-${Date.now()}.png`;
        link.href = dataUrl;
        link.click();
      } catch (err) {
        console.error('Failed to export chart', err);
      }
    }
  };

  return (
    <div className={`dynamic-chart-wrapper ${compact ? 'compact' : 'full'}`} ref={chartRef}>
      {!compact && validated.title && !hideTitle && (
        <div className="dc-header">
          <div>
            <h3 className="dc-title">{validated.title}</h3>
            {validated.subtitle && <p className="dc-subtitle">{validated.subtitle}</p>}
          </div>
          <button className="dc-export-btn" onClick={handleExport} title="Download Chart">
            <HiDownload />
          </button>
        </div>
      )}
      <div className="dc-canvas">
        {ChartContent}
      </div>
      {!compact && validated.insight && (
        <div className="dc-insight">
          <span className="dc-insight-icon">🤖</span>
          <span className="dc-insight-text">{validated.insight}</span>
        </div>
      )}
    </div>
  );
};

export default DynamicChart;
